/**
 * ProjectContextSection.tsx — el contexto del proyecto para EvoBrain
 * (reemplaza al Brief, 2026-08-29).
 *
 * Todo lo que le da contexto a EvoBrain para trabajar el proyecto: notas,
 * páginas web de referencia, documentos (PDF/Word/texto) y referencias
 * visuales. Se guarda en project.briefData — el mismo almacenamiento que
 * usaba el brief, sin el formulario de campos ni el ciclo de aprobación.
 *
 * Al lado, la tarjeta "Plan de contenido": elegís página única o varias
 * páginas (con sus nombres) y Crear plan dispara la generación. Ningún paso
 * condiciona al otro — Diseño funciona sin plan y sin contexto.
 */
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailSection } from '../ui/DetailSection.js';
import { FileUploadZone } from '../ui/FileUploadZone.js';
import { Textarea } from '../ui/Textarea.js';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Chip } from '../ui/Chip.js';
import { Icon } from '../Icon.js';
import { SegmentedControl } from '../ui/SegmentedControl.js';
import { useToast } from '../ui/Toast.js';
import {
  useSaveBrief, useDeleteAsset, useGenerateContentPlan,
  type ProjectRow,
} from '../../hooks/useProjects.js';

interface ContextData {
  references?: { links?: string[]; attachments?: string[] };
  images?: string[];
  documents?: string[];
  pages?: string[];
  notes?: string;
  [k: string]: unknown; // campos del brief viejo se conservan intactos
}

function parseContext(raw: string | null): ContextData {
  if (!raw) return {};
  try { return JSON.parse(raw) as ContextData; } catch { return {}; }
}

function FieldBlock({ label, hint, children, last }: { label: string; hint?: string; children: ReactNode; last?: boolean }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-3) 0', borderBottom: last ? 'none' : '1px solid var(--evo-stroke)' }}>
      <span style={{ fontSize: 'var(--evo-fs-label)', fontWeight: 'var(--evo-fw-medium)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-2)' }}>{label}</span>
      {hint && <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', fontFamily: 'var(--evo-font)', lineHeight: 1.5 }}>{hint}</span>}
      {children}
    </div>
  );
}

export function ProjectContextSection({ project }: { project: ProjectRow }) {
  const { t } = useTranslation();
  const saveBriefMut = useSaveBrief();
  const deleteAssetMut = useDeleteAsset();

  const initial = useMemo(() => parseContext(project.briefData), [project.briefData]);
  const ctxRef = useRef<ContextData>(initial);
  const [ctx, setCtx] = useState<ContextData>(initial);
  const [newLink, setNewLink] = useState('');

  function patchLocal(updates: Partial<ContextData>) {
    const next = { ...ctxRef.current, ...updates };
    ctxRef.current = next;
    setCtx(next);
  }

  async function autoSave(updates: Partial<ContextData>) {
    patchLocal(updates);
    await saveBriefMut.mutateAsync({ id: project.id, briefData: ctxRef.current });
  }

  async function deleteAsset(url: string, field: 'images' | 'documents' | 'refAttachments') {
    await deleteAssetMut.mutateAsync({ projectId: project.id, path: url });
    const c = ctxRef.current;
    if (field === 'images') await autoSave({ images: (c.images ?? []).filter(p => p !== url) });
    else if (field === 'documents') await autoSave({ documents: (c.documents ?? []).filter(p => p !== url) });
    else await autoSave({ references: { ...c.references, attachments: (c.references?.attachments ?? []).filter(p => p !== url) } });
  }

  function addLink() {
    const url = newLink.trim();
    if (!url) return;
    const links = [...(ctxRef.current.references?.links ?? []), url];
    void autoSave({ references: { ...ctxRef.current.references, links } });
    setNewLink('');
  }

  const uploadUrl = `/api/projects/${project.id}/assets`;

  return (
    <DetailSection title={t('projects.context.title')} divider={false}>
      <p style={{ margin: '0 0 var(--evo-space-2)', fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', lineHeight: 1.65, color: 'var(--evo-fg-3)' }}>
        {t('projects.context.intro')}
      </p>

      <FieldBlock label={t('projects.context.notes')} hint={t('projects.context.notesHint')}>
        <div onBlur={() => { void saveBriefMut.mutateAsync({ id: project.id, briefData: ctxRef.current }); }}>
          <Textarea
            value={ctx.notes ?? ''}
            onChange={v => patchLocal({ notes: v })}
            placeholder={t('projects.context.notesPlaceholder')}
            rows={4}
            fullWidth
            resize="vertical"
          />
        </div>
      </FieldBlock>

      <FieldBlock label={t('projects.context.links')} hint={t('projects.context.linksHint')}>
        {(ctx.references?.links ?? []).length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-1)' }}>
            {(ctx.references?.links ?? []).map(link => (
              <Chip
                key={link}
                label={link}
                onRemove={() => {
                  const links = (ctxRef.current.references?.links ?? []).filter(l => l !== link);
                  void autoSave({ references: { ...ctxRef.current.references, links } });
                }}
              />
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 'var(--evo-space-2)', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Input
              type="url"
              size="sm"
              fullWidth
              value={newLink}
              onChange={setNewLink}
              placeholder={t('projects.context.linkPlaceholder')}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLink(); } }}
            />
          </div>
          <Button variant="outline" tone="muted" size="sm" onClick={addLink}>
            {t('projects.context.addLink')}
          </Button>
        </div>
      </FieldBlock>

      <FieldBlock label={t('projects.context.documents')} hint={t('projects.context.documentsHint')}>
        <FileUploadZone
          uploadUrl={uploadUrl}
          value={ctx.documents ?? []}
          onUploaded={paths => { void autoSave({ documents: [...(ctxRef.current.documents ?? []), ...paths] }); }}
          onDelete={url => { void deleteAsset(url, 'documents'); }}
          acceptedTypes=".pdf,.doc,.docx,.txt,.md,application/pdf,text/plain,text/markdown,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          dragLabel={t('projects.brief.upload.dragOrClick')}
        />
      </FieldBlock>

      <FieldBlock label={t('projects.context.images')} hint={t('projects.context.imagesHint')} last>
        <FileUploadZone
          uploadUrl={uploadUrl}
          value={ctx.images ?? []}
          onUploaded={paths => { void autoSave({ images: [...(ctxRef.current.images ?? []), ...paths] }); }}
          onDelete={url => { void deleteAsset(url, 'images'); }}
          acceptedTypes="image/*"
          dragLabel={t('projects.brief.upload.dragOrClick')}
        />
      </FieldBlock>
    </DetailSection>
  );
}

// ── Arranque del plan de contenido ───────────────────────────────────────────
// Página única o varias páginas (con nombres libres) + Crear plan. El modo
// escribe briefData.pages — la fuente que el generador ya lee.

export function PlanKickoffCard({ project, onCreated }: { project: ProjectRow; onCreated: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const saveBriefMut = useSaveBrief();
  const generateMut = useGenerateContentPlan();

  const ctx = useMemo(() => parseContext(project.briefData), [project.briefData]);
  const storedPages = ctx.pages ?? [];
  const [mode, setMode] = useState<'single' | 'multi'>(storedPages.length > 1 ? 'multi' : 'single');
  const [pages, setPages] = useState<string[]>(storedPages.length > 0 ? storedPages : ['Inicio']);
  const [newPage, setNewPage] = useState('');
  const hasPlan = project.contentPlan != null;

  function addPage() {
    const clean = newPage.trim();
    if (!clean || pages.includes(clean)) return;
    setPages(p => [...p, clean]);
    setNewPage('');
  }

  async function createPlan() {
    const finalPages = mode === 'single' ? [pages[0] ?? 'Inicio'] : pages;
    await saveBriefMut.mutateAsync({ id: project.id, briefData: { ...ctx, pages: finalPages } });
    generateMut.mutate(project.id, {
      onSuccess: () => onCreated(),
      onError: err => toast.error(err instanceof Error ? err.message : String(err)),
    });
  }

  return (
    <DetailSection title={t('projects.context.planTitle')} divider={false}>
      <p style={{ margin: '0 0 var(--evo-space-3)', fontSize: 'var(--evo-fs-body)', fontFamily: 'var(--evo-font)', lineHeight: 1.65, color: 'var(--evo-fg-3)' }}>
        {t('projects.context.planIntro')}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
        <SegmentedControl<'single' | 'multi'>
          value={mode}
          onChange={setMode}
          size="sm"
          options={[
            { value: 'single', label: t('projects.context.singlePage') },
            { value: 'multi', label: t('projects.context.multiPage') },
          ]}
        />
        {mode === 'multi' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--evo-space-1)' }}>
              {pages.map(p => (
                <Chip key={p} label={p} {...(pages.length > 1 ? { onRemove: () => setPages(ps => ps.filter(x => x !== p)) } : {})} />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 'var(--evo-space-2)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Input
                  size="sm" fullWidth value={newPage} onChange={setNewPage}
                  placeholder={t('projects.brief.pages.placeholder')}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPage(); } }}
                />
              </div>
              <Button variant="outline" tone="muted" size="sm" onClick={addPage}>
                {t('projects.brief.pages.add')}
              </Button>
            </div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <Button
            tone="cyan"
            variant="solid"
            size="sm"
            loading={generateMut.isPending}
            onClick={() => { void createPlan(); }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Icon name="bolt" size={12} />
              {hasPlan ? t('projects.context.regeneratePlan') : t('projects.context.createPlan')}
            </span>
          </Button>
          {generateMut.isPending && (
            <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)' }}>
              {t('projects.context.generating')}
            </span>
          )}
        </div>
      </div>
    </DetailSection>
  );
}
